/**
 * RequestsPanel — вкладка «Заявки».
 *
 * Зачем. Когда бот доводит разговор до передачи человеку, заявка уходит тремя
 * путями: запись в базе Тони, строка в базе кабинета и карточка в Telegram
 * владельцу. Первые две до 13.08 никто не видел — заявки копились, а
 * единственным способом узнать о них был телеграм-пинг. Пропустил карточку —
 * потерял клиента.
 *
 * Статусы те же, что в модели: новая → принята → в работе → закрыта, плюс два
 * отказных. Меняются одним нажатием: заявку берут в работу на бегу.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Phone } from 'lucide-react'

import { agentsApi } from '@/api/client'

const STATUSES: { key: string; label: string; tone: string }[] = [
  { key: 'new', label: 'Новая', tone: 'bg-amber-100 text-amber-900' },
  { key: 'accepted', label: 'Принята', tone: 'bg-sky-100 text-sky-900' },
  { key: 'in_progress', label: 'В работе', tone: 'bg-indigo-100 text-indigo-900' },
  { key: 'done', label: 'Закрыта', tone: 'bg-emerald-100 text-emerald-900' },
  { key: 'postponed', label: 'Отложена', tone: 'bg-slate-100 text-slate-700' },
  { key: 'not_client', label: 'Не клиент', tone: 'bg-slate-100 text-slate-700' },
]

const CHANNEL_LABEL: Record<string, string> = {
  web_form: 'сайт', web: 'сайт', telegram: 'Telegram', max: 'MAX', avito: 'Авито',
}

function when(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

export function RequestsPanel({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['agent-requests', slug],
    queryFn: () => agentsApi.requests(slug),
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      agentsApi.setRequestStatus(slug, id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-requests', slug] }),
  })

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Загружаем заявки…
      </div>
    )
  }

  const items = data ?? []
  if (items.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        Заявок пока нет. Сюда попадёт каждый разговор, который бот довёл до
        передачи человеку: с телефоном, с записью на замер или по просьбе
        соединить с менеджером.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground">всего заявок: {items.length}</div>
      {items.map((r) => {
        const st = STATUSES.find((s) => s.key === r.status)
        return (
          <div key={r.id} className="space-y-2 rounded-md border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${
                st?.tone ?? 'bg-slate-100 text-slate-700'}`}>
                {st?.label ?? r.status}
              </span>
              <span className="text-sm font-medium">
                {r.contact_name || 'Без имени'}
              </span>
              {r.contact_phone && (
                <a
                  href={`tel:${r.contact_phone}`}
                  className="flex items-center gap-1 text-sm text-sky-700 hover:underline"
                >
                  <Phone className="h-3 w-3" /> {r.contact_phone}
                </a>
              )}
              <span className="ml-auto text-[11px] text-muted-foreground">
                {CHANNEL_LABEL[r.channel] ?? r.channel} · {when(r.created_at)}
              </span>
            </div>

            {r.summary && (
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {r.summary}
              </p>
            )}

            <div className="flex flex-wrap gap-1">
              {STATUSES.filter((s) => s.key !== r.status).map((s) => (
                <button
                  key={s.key}
                  type="button"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate({ id: r.id, status: s.key })}
                  className="rounded border px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted disabled:opacity-50"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
